/**
 * @license
 * Copyright (c) 2008 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Dimension, MarkerShapes } from '../UNSAFE_visTypes/common';
import { Point } from '../PRIVATE_visSVGUtils';
import { Scale, Group, ChartSeriesData, ChartItemData, Info } from '../UNSAFE_visTypes/chart';
import { ColorProps } from '../UNSAFE_interpolations/colors';
import { colorSchemeVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { rgb, rgba } from '../../UNSAFE_Theme';
import { getGroupCenterCoord } from '../../utils/PRIVATE_chartUtils/layoutUtils';
import { CommonLineType } from '#utils/UNSAFE_visTypes/common/common.types';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';

const LABEL_OFFSET = 15;

/**
 * Returns the cumulative series for stacked chart.
 */
export function getCumulativeSeries<K extends string | number, D extends { value: number; id: K }>(
  numSeries: number,
  numGroups: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  hiddenIds: Set<number | string>,
  isLog: boolean
) {
  const series: (number | undefined)[][] = Array(numSeries)
    .fill(numGroups)
    .map(() => {
      return new Array(numGroups);
    });
  for (let groupIndex = 0; groupIndex < numGroups; groupIndex += 1) {
    let prevValidTotal = undefined;
    for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex += 1) {
      const item = getDataItem(seriesIndex, groupIndex);
      if (
        !item ||
        hiddenIds?.has(item.id) ||
        (isLog && item.value !== undefined && item.value <= 0)
      ) {
        series[seriesIndex][groupIndex] = undefined;
        continue;
      }

      if (seriesIndex > 0 && prevValidTotal != null) {
        const prev = series[seriesIndex - 1][groupIndex];
        if (prev != null) {
          series[seriesIndex][groupIndex] = prev + item.value;
        } else {
          series[seriesIndex][groupIndex] = prevValidTotal + item.value;
        }
      } else {
        series[seriesIndex][groupIndex] = item.value;
        prevValidTotal = item.value;
      }
    }
  }
  return series;
}

/**
 * Returns the series data.
 */
export function getSeriesData<
  K extends string | number,
  D extends { x?: number | string; y?: number; value?: number; id: K }
>(
  numSeries: number,
  numGroups: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  hiddenIds: Set<number | string>,
  isLog: boolean
) {
  const series: (number[] | { x: number; y: number }[])[] = [];
  for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex += 1) {
    const currentSeries: number[] | { x: number; y: number }[] = [];
    for (let groupIndex = 0; groupIndex < numGroups; groupIndex += 1) {
      const item = getDataItem(seriesIndex, groupIndex);
      if (
        !item ||
        hiddenIds?.has(item.id) ||
        (isLog && item.value !== undefined && item.value <= 0)
      ) {
        continue;
      }
      currentSeries[groupIndex] =
        item.value !== undefined ? item.value : { x: item.x! as number, y: item.y! }; // TODO : Need fix for x as number
    }
    series.push(currentSeries);
  }
  return series;
}

/**
 * Returns the markertype order.
 */
export function getMarkers(): MarkerShapes[] {
  return ['square', 'circle', 'diamond', 'plus', 'triangleDown', 'triangleUp'];
}

/**
 * Returns the marker fill and stroke depending on the state.
 */
export function getMarkerFillAndStroke( // TODO : JET-62244 - Replace getMarkerFillAndStroke function with classes.
  isMarkerDisplayed: boolean,
  isSelectionEnabled: boolean,
  isHovered: boolean,
  isFocused: boolean,
  isSelected: boolean,
  seriesColor: ColorProps['color'],
  itemColor?: ColorProps['color']
) {
  let fill;
  let stroke;
  let outerStroke;

  if (isSelectionEnabled) {
    if (isSelected) {
      fill = itemColor || seriesColor;
      outerStroke = rgb(colorSchemeVars.palette.neutral[130]);
      stroke = rgb(colorSchemeVars.palette.neutral[0]);
    } else if (isHovered || isFocused) {
      stroke = itemColor || seriesColor;
      fill = rgb(colorSchemeVars.palette.neutral[0]);
    } else {
      if (isMarkerDisplayed) {
        fill = itemColor || seriesColor;
        stroke = rgb(colorSchemeVars.palette.neutral[0]);
      } else {
        fill = rgba(colorSchemeVars.palette.neutral[0], 0);
        stroke = rgba(colorSchemeVars.palette.neutral[0], 0);
      }
    }
  } else if (isMarkerDisplayed) {
    fill = itemColor || seriesColor;
    stroke = rgb(colorSchemeVars.palette.neutral[0]);
  } else {
    stroke = isFocused ? itemColor || seriesColor : rgba(colorSchemeVars.palette.neutral[0], 0);
    fill = rgba(colorSchemeVars.palette.neutral[0], 0);
  }
  return { fill, stroke, outerStroke };
}

/**
 * Returns the coordinates of line or area based on orientation and size of the chart.
 */
export function getLineAreaSegments<
  K extends string | number,
  D extends { x?: number | string; y?: number; value?: number; id: K }
>(
  yScale: Scale,
  xScale: Scale,
  data: (number | undefined)[] | { x: number; y: number }[],
  startIndex: number,
  endIndex: number,
  isLog: boolean,
  groups: Group[],
  seriesIndex: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency',
  hasXYValues?: boolean
): Point[][] {
  const segments: Point[][] = [];
  let currentPath: Point[] = [];

  for (let groupIndex = startIndex; groupIndex < endIndex + 1; groupIndex += 1) {
    let item, value, valueX, valueY;
    if (hasXYValues) {
      item = data[groupIndex] as { x: number; y: number };
      valueX = item?.x;
      valueY = item?.y;
    } else {
      item = data[groupIndex] as number;
      value = item;
    }
    if (item == null || (isLog && value! <= 0 && valueX! <= 0 && valueY! <= 0)) {
      segments.push(currentPath);
      currentPath = [];
      continue;
    }
    let x, y;

    if (hasXYValues) {
      x = xScale.transform(valueX!);
      y = yScale.transform(valueY!);
    } else {
      x = getGroupCenterCoord<K, D>(
        groups[groupIndex],
        groupIndex,
        xScale,
        getDataItem(seriesIndex, groupIndex)!,
        timeAxisType
      );
      y = yScale.transform(value!);
    }
    currentPath.push([x, y]);
    if (groupIndex === endIndex) {
      segments.push(currentPath);
    }
  }
  return segments;
}

/**
 * Returns the markertype order.
 */
export function getLabelPosition(
  labelPosition: 'belowMarker' | 'aboveMarker' | 'beforeMarker' | 'afterMarker' | 'center',
  tx?: number,
  ty?: number,
  labelDims?: Dimension,
  isRtl?: boolean
) {
  const halfLabelWidth = labelDims?.width! / 2;

  if (labelPosition === 'aboveMarker') {
    ty = ty! - LABEL_OFFSET;
  } else if (labelPosition === 'belowMarker') {
    ty = ty! + LABEL_OFFSET;
  } else if (labelPosition === 'afterMarker') {
    tx = isRtl ? tx! - LABEL_OFFSET - halfLabelWidth : tx! + LABEL_OFFSET + halfLabelWidth;
  } else if (labelPosition === 'beforeMarker') {
    tx = isRtl ? tx! + LABEL_OFFSET + halfLabelWidth : tx! - LABEL_OFFSET - halfLabelWidth;
  }

  return { x: tx, y: ty };
}

// JET-71237 optimize this function
export const findSeriesGroupIndexFromId = <K, D>(
  series: ChartSeriesData<K, D>[],
  id: K,
  levelsArray: GroupLevelItem[][],
  getDataItem?: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined
) => {
  const leafGroups = levelsArray[levelsArray.length - 1];
  for (let i = 0; i < series.length; i++) {
    for (let j = 0; j < leafGroups.length; j++) {
      const item = getDataItem && getDataItem(i, j, leafGroups[j].path);
      if (item && (item as unknown as ChartItemData<K>).id === id) {
        return {
          seriesIndex: i,
          groupIndex: j
        };
      }
    }
  }
  return {
    seriesIndex: 0,
    groupIndex: 0
  };
};
export function getCmdPoints(
  lineSegmentCoords: Point[],
  lineType: string,
  isHoriz: boolean,
  isRtl?: boolean,
  groupWidth?: number,
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency',
  isArea?: boolean
) {
  const pointsArrays = [];
  let points: number[] = [];
  pointsArrays.push(points);
  const isCentered = lineType == 'centeredStepped' || lineType == 'centeredSegmented';
  const isParallel = isCentered || lineType == 'stepped' || lineType == 'segmented';
  const dir = isRtl && !isHoriz ? -1 : 1;
  let coord, xCoord, yCoord, lastCoord, finalXCoord;
  for (let i = 0; i < lineSegmentCoords.length; i++) {
    if (lineSegmentCoords[i] == null) {
      if (timeAxisType !== 'mixedFrequency') {
        // Draw the last step
        if (isParallel && lastCoord) {
          finalXCoord = isCentered
            ? lastCoord[0] + 0.5 * groupWidth! * dir
            : lastCoord[0] + groupWidth! * dir;
          pushCoords(points, finalXCoord, lastCoord[1], isHoriz);
        }
      }
      // Start a new list of points, except in ADF and MAF mixed freq where we want to connect points across nulls.
      points = [];
      pointsArrays.push(points);
      lastCoord = null;
      continue;
    }
    coord = lineSegmentCoords[i];
    xCoord = isCentered ? coord[0] - (groupWidth! / 2) * dir : coord[0];
    yCoord = coord[1];

    if (lastCoord && isParallel) {
      pushCoords(points, xCoord, lastCoord[1], isHoriz); // draw the step
    }
    if (!isArea && (lineType === 'segmented' || lineType === 'centeredSegmented')) {
      points = [];
      pointsArrays.push(points);
    }

    pushCoords(points, xCoord, yCoord, isHoriz);
    lastCoord = coord;
  }
  if (isParallel && lastCoord) {
    finalXCoord = isCentered
      ? lastCoord[0] + 0.5 * groupWidth! * dir
      : lastCoord[0] + groupWidth! * dir;
    pushCoords(points, finalXCoord, lastCoord[1], isHoriz);
  }
  return pointsArrays.join(' ');
}

export function isNonCurvedLine(
  lineType: CommonLineType | 'centeredStepped' | 'centeredSegmented'
) {
  return ['straight', 'stepped', 'segmented', 'centeredStepped', 'centeredSegmented'].includes(
    lineType
  );
}
export function pushCoords(points: number[], x: number, y: number, isHoriz: boolean) {
  if (isHoriz) {
    points.push(Math.round(y * 10) / 10, Math.round(x * 10) / 10);
  } else {
    points.push(Math.round(x * 10) / 10, Math.round(y * 10) / 10);
  }
}

export function getActiveInfo(focusedItemInfo?: Info, hoveredItemInfo?: Info) {
  return focusedItemInfo?.isCurrent
    ? focusedItemInfo
    : hoveredItemInfo?.isCurrent &&
      ((hoveredItemInfo.seriesIndex !== undefined && hoveredItemInfo.groupIndex !== undefined) ||
        hoveredItemInfo?.refObjId !== undefined)
    ? hoveredItemInfo
    : undefined;
}
